import React, { useMemo } from "react";

import InvoiceModal from "./InvoiceModal";
import PaymentReceiptModal from "./PaymentReceiptModal";

import {
  calculateBill,
  createReceiptNumber,
  formatMoney,
  toAmount,
} from "../../services/billingService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const formatDateTime = (value) => {
  if (!value) return "-";

  let date;

  if (typeof value?.toDate === "function") {
    date = value.toDate();
  } else if (value?.seconds) {
    date = new Date(value.seconds * 1000);
  } else {
    date = new Date(value);
  }

  if (
    !date ||
    Number.isNaN(date.getTime())
  ) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getPrimaryGuest = (stay = {}) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length > 0
  ) {
    return (
      stay.guests.find(
        (guest) =>
          guest?.isPrimary === true
      ) || stay.guests[0]
    );
  }

  return {
    name:
      stay.guestName ||
      stay.name ||
      "Guest",

    phoneNumber:
      stay.phoneNumber ||
      stay.phone ||
      "",
  };
};

const getCheckoutPayment = (
  stay = {}
) => {
  if (
    stay.checkoutPaymentEntry &&
    toAmount(
      stay.checkoutPaymentEntry.amount
    ) > 0
  ) {
    return stay.checkoutPaymentEntry;
  }

  const payments =
    Array.isArray(stay.payments)
      ? stay.payments
      : [];

  return (
    [...payments]
      .reverse()
      .find(
        (payment) =>
          payment?.source ===
            "checkout_payment" ||
          payment?.title ===
            "Checkout Payment"
      ) || null
  );
};

/* -------------------------------------------------------------------------- */
/*                         Checkout Success Modal                             */
/* -------------------------------------------------------------------------- */

function CheckoutSuccessModal({
  stay,
  hotelId,
  hotelName,
  onClose,
}) {
  const [invoiceOpen, setInvoiceOpen] =
    React.useState(
      stay?.printInvoiceAfterCheckout !==
        false
    );

  const [
    receiptOpen,
    setReceiptOpen,
  ] = React.useState(
    Boolean(
      stay?.printReceiptAfterCheckout &&
        getCheckoutPayment(stay)
    )
  );

  const primaryGuest = useMemo(
    () => getPrimaryGuest(stay),
    [stay]
  );

  const bill = useMemo(
    () =>
      calculateBill(stay, {
        discountAmount:
          stay?.discountAmount,

        gstRate: stay?.gstRate,

        paid:
          stay?.paidTotal ??
          stay?.paid,
      }),
    [stay]
  );

  const checkoutPayment = useMemo(
    () => getCheckoutPayment(stay),
    [stay]
  );

  if (!stay) {
    return null;
  }

  return (
    <>
      <div
        style={overlayStyle}
        onMouseDown={(event) => {
          if (
            event.target ===
            event.currentTarget
          ) {
            onClose?.();
          }
        }}
      >
        <div style={modalStyle}>
          <header style={headerStyle}>
            <div>
              <h2 style={titleStyle}>
                Checkout Complete ✅
              </h2>

              <div style={subtitleStyle}>
                {hotelName ||
                  stay.hotelName ||
                  "Hotel"}{" "}
                • Room {stay.room || "-"}
              </div>
            </div>

            <button
              type="button"
              onClick={() =>
                onClose?.()
              }
              style={closeButtonStyle}
            >
              ×
            </button>
          </header>

          <section style={successBoxStyle}>
            <strong>
              Guest checkout successfully
              save ho gaya.
            </strong>

            <div style={successTextStyle}>
              Room status, invoice, payment
              aur Day Book records update ho
              gaye hain.
            </div>
          </section>

          <section style={summaryGridStyle}>
            <SummaryBox
              label="Guest"
              value={
                primaryGuest?.name ||
                "Guest"
              }
            />

            <SummaryBox
              label="Phone"
              value={
                primaryGuest?.phoneNumber ||
                "-"
              }
            />

            <SummaryBox
              label="Checkout Time"
              value={formatDateTime(
                stay.actualCheckOutAt ||
                  stay.checkedOutAt
              )}
            />

            <SummaryBox
              label="Invoice No."
              value={
                stay.invoiceNumber || "-"
              }
            />

            <SummaryBox
              label="Grand Total"
              value={formatMoney(
                stay.grandTotal ??
                  bill.grandTotal
              )}
            />

            <SummaryBox
              label="Paid"
              value={formatMoney(
                stay.paidTotal ??
                  bill.paid
              )}
            />

            <SummaryBox
              label="Balance"
              value={formatMoney(
                stay.balance ??
                  bill.balance
              )}
              danger={
                toAmount(
                  stay.balance ??
                    bill.balance
                ) > 0
              }
            />

            <SummaryBox
              label="Room Status"
              value={
                stay.sendRoomForCleaning !==
                false
                  ? "Cleaning Pending"
                  : "Vacant"
              }
            />
          </section>

          {toAmount(
            stay.balance ??
              bill.balance
          ) > 0 && (
            <section
              style={pendingWarningStyle}
            >
              <strong>
                Pending Balance:
                {" "}
                {formatMoney(
                  stay.balance ??
                    bill.balance
                )}
              </strong>

              <div style={{ marginTop: 4 }}>
                Reason:{" "}
                {stay.pendingReason ||
                  "Reason not available"}
              </div>
            </section>
          )}

          <section style={actionsGridStyle}>
            <button
              type="button"
              onClick={() =>
                setInvoiceOpen(true)
              }
              style={invoiceButtonStyle}
            >
              🧾 Open Invoice
            </button>

            <button
              type="button"
              disabled={!checkoutPayment}
              onClick={() =>
                setReceiptOpen(true)
              }
              style={{
                ...receiptButtonStyle,

                opacity: checkoutPayment
                  ? 1
                  : 0.5,

                cursor: checkoutPayment
                  ? "pointer"
                  : "not-allowed",
              }}
            >
              💳 Open Payment Receipt
            </button>

            <button
              type="button"
              onClick={() =>
                onClose?.()
              }
              style={doneButtonStyle}
            >
              Done
            </button>
          </section>
        </div>
      </div>

      {invoiceOpen && (
        <InvoiceModal
          stay={stay}
          bill={{
            ...bill,

            grandTotal:
              stay.grandTotal ??
              bill.grandTotal,

            paid:
              stay.paidTotal ??
              bill.paid,

            balance:
              stay.balance ??
              bill.balance,
          }}
          invoiceNumber={
            stay.invoiceNumber ||
            "Invoice"
          }
          hotelName={
            hotelName ||
            stay.hotelName
          }
          onClose={() =>
            setInvoiceOpen(false)
          }
        />
      )}

      {receiptOpen &&
        checkoutPayment && (
          <PaymentReceiptModal
            stay={stay}
            payment={
              checkoutPayment
            }
            hotelName={
              hotelName ||
              stay.hotelName
            }
            receiptNumber={
              checkoutPayment
                .receiptNumber ||
              createReceiptNumber(
                hotelId
              )
            }
            onClose={() =>
              setReceiptOpen(false)
            }
          />
        )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Component                                */
/* -------------------------------------------------------------------------- */

function SummaryBox({
  label,
  value,
  danger = false,
}) {
  return (
    <div style={summaryBoxStyle}>
      <span style={summaryLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...summaryValueStyle,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 26000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.78)",
};

const modalStyle = {
  width: "min(760px,100%)",
  maxHeight: "94vh",

  overflowY: "auto",

  padding: 18,

  borderRadius: 20,

  background: "#ffffff",

  boxShadow:
    "0 28px 90px rgba(0,0,0,.35)",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,

  paddingBottom: 13,

  borderBottom:
    "1px solid #e2e8f0",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 12,
  fontWeight: 700,
};

const closeButtonStyle = {
  width: 38,
  height: 38,

  border: 0,
  borderRadius: 10,

  background: "#f1f5f9",
  color: "#334155",

  fontSize: 25,

  cursor: "pointer",
};

const successBoxStyle = {
  marginTop: 14,
  padding: 13,

  borderRadius: 12,

  border:
    "1px solid #bbf7d0",

  background: "#ecfdf5",
  color: "#166534",
};

const successTextStyle = {
  marginTop: 4,
  fontSize: 11,
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(145px,1fr))",

  gap: 9,

  marginTop: 14,
};

const summaryBoxStyle = {
  padding: 11,

  borderRadius: 11,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",
};

const summaryLabelStyle = {
  display: "block",

  marginBottom: 4,

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  display: "block",

  fontSize: 12,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const pendingWarningStyle = {
  marginTop: 13,
  padding: 11,

  borderRadius: 11,

  border:
    "1px solid #fdba74",

  background: "#fff7ed",
  color: "#9a3412",

  fontSize: 12,
};

const actionsGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(170px,1fr))",

  gap: 9,

  marginTop: 15,
};

const commonButtonStyle = {
  minHeight: 44,

  padding: "10px 13px",

  border: 0,
  borderRadius: 10,

  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const invoiceButtonStyle = {
  ...commonButtonStyle,
  background: "#0f766e",
};

const receiptButtonStyle = {
  ...commonButtonStyle,
  background: "#2563eb",
};

const doneButtonStyle = {
  ...commonButtonStyle,
  background: "#475569",
};

export default CheckoutSuccessModal;